import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useDocument, useJobs, useQuestions, type JobOut } from "@/api/queries";
import { PipelineStepper } from "@/components/pipeline-stepper";
import { QuestionDetail } from "@/components/question-detail";
import { RunStateChip } from "@/components/run-state-chip";
import { Badge } from "@/components/ui/badge";
import { Drawer } from "@/components/ui/drawer";
import { Callout, Empty, ErrorNote, PaneHeader, Panel } from "@/components/ui/panel";
import { Stat } from "@/components/ui/stat";
import { formatDate } from "@/lib/utils";

/**
 * The live tracker for one upload — DESIGN §7/§6, answering "what happened to the N
 * questions I just ingested" from the moment the chunks land through clustering and,
 * since a pipeline run does not stop meaning anything once pending_review starts,
 * through however much of human review has happened since. Linked from the Ingest
 * screen's runs table (`Ingest.tsx`) and revisitable directly by URL — a batch can
 * take from seconds to the rest of a UTC day (the quota backoff), so this is a page,
 * not a toast that is gone the moment someone navigates away.
 *
 * `useDocument` is the only thing polled at speed; the question table and feed read
 * off the same response rather than each running their own interval.
 */
export default function IngestRun() {
  const { sourceDoc } = useParams<{ sourceDoc: string }>();
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const { data: doc, isLoading, error } = useDocument(sourceDoc ?? null);
  // The pipeline job queue is bank-wide, not per-document (one `pipeline` job carries
  // whatever is pending for everyone) — so "paused for quota" below is reported as a
  // fact about the shared worker, not a claim that this specific run is the cause.
  const { data: pipelineJobs } = useJobs(1, "pipeline");
  const paused = quotaPause(pipelineJobs?.items[0]);

  const { data: questionsPage } = useQuestions(
    sourceDoc ? { source_doc: sourceDoc, limit: 200 } : { limit: 0 },
  );
  const questions = questionsPage?.items ?? [];

  if (!sourceDoc) return <Empty>No document named.</Empty>;
  if (isLoading) return <Empty>loading…</Empty>;
  if (error) return <ErrorNote error={error} />;
  if (!doc) return <Empty>Not found.</Empty>;

  const waitingNow = (doc.questions.pending_review ?? 0) + (doc.questions.stale ?? 0);
  const duplicate = doc.questions.duplicate ?? 0;
  const approved = doc.questions.approved ?? 0;
  const rejected = doc.questions.rejected ?? 0;
  const failed = doc.questions.failed ?? 0;
  const inPipeline = Math.max(doc.question_total - waitingNow - duplicate - approved - rejected - failed, 0);

  return (
    <div className="flex h-full flex-col gap-3 overflow-auto p-4">
      <div>
        <Link to="/ingest" className="text-bank-muted hover:text-bank-ink text-xs">
          ← Ingest
        </Link>
        <div className="mt-1 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-base font-semibold tracking-tight break-all">{doc.source_doc}</h1>
            <div className="text-bank-muted mt-1 flex items-center gap-2 text-xs">
              {doc.expected_stack && (
                <span className="border-bank-border-strong bg-bank-surface-2 rounded-full border px-2 py-0.5 font-mono text-[11px]">
                  {doc.expected_stack}
                </span>
              )}
              <span>uploaded {formatDate(doc.uploaded_at)}</span>
            </div>
          </div>
          <RunStateChip doc={doc} className="shrink-0" />
        </div>
      </div>

      {paused && (
        <Callout tone="attention">
          <strong>Paused — daily quota reached</strong> for <code>{paused.model}</code>. The shared pipeline
          worker resumes automatically once quota frees up or the day rolls over at UTC midnight — this run's
          cursor is untouched and nothing here needs retrying by hand.
        </Callout>
      )}

      <Panel className="p-5">
        <PipelineStepper doc={doc} />
      </Panel>

      <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-6">
        <Stat label="in pipeline" value={inPipeline} />
        <Stat label="pending review" value={waitingNow} flag={waitingNow > 0} />
        <Stat label="approved" value={approved} />
        <Stat label="duplicate" value={duplicate} />
        <Stat label="rejected" value={rejected} />
        <Stat label="failed" value={failed} />
      </div>

      <div className="grid min-h-0 flex-1 gap-3 lg:grid-cols-[1.6fr_1fr]">
        <Panel className="flex min-h-0 flex-col overflow-hidden">
          <PaneHeader
            title={`Questions from this upload — ${doc.question_total} total, showing ${questions.length}`}
          />
          <div className="min-h-0 flex-1 overflow-auto">
            {questions.length === 0 ? (
              <Empty>No questions extracted yet.</Empty>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {questions.map((q) => (
                    <tr
                      key={q.id}
                      onClick={() => setSelectedId(q.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setSelectedId(q.id);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-label={`View question ${q.id}`}
                      className="border-bank-border hover:bg-bank-surface-2 focus-visible:outline-bank-accent cursor-pointer border-t transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2"
                    >
                      <td className="text-bank-muted px-3 py-2 font-mono text-[11px] tabular-nums">
                        #{q.id}
                      </td>
                      <td className="question-text max-w-md px-3 py-2 text-[13px]">{q.question_text}</td>
                      <td className="px-3 py-2">
                        <Badge value={q.status} />
                      </td>
                      <td className="text-bank-muted px-3 py-2 font-mono text-[11px]">
                        {q.canonical_id
                          ? `→ #${q.canonical_id}`
                          : (q.cluster_size ?? 1) > 1
                            ? "canonical"
                            : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Panel>

        <Panel className="flex min-h-0 flex-col overflow-hidden">
          <PaneHeader title="Activity" />
          <div className="min-h-0 flex-1 overflow-auto">
            {doc.recent_transitions.length === 0 ? (
              <Empty>Nothing yet.</Empty>
            ) : (
              <ul>
                {doc.recent_transitions.map((t) => (
                  <li
                    key={t.id}
                    onClick={() => setSelectedId(t.question_id)}
                    className="border-bank-border hover:bg-bank-surface-2 flex cursor-pointer items-start gap-2 border-t px-3 py-2 text-[12.5px] transition-colors"
                  >
                    <span className="text-bank-muted font-mono text-[11px] tabular-nums">
                      #{t.question_id}
                    </span>
                    <span className="min-w-0 flex-1">
                      {t.from_status ?? "∅"} → <span className="font-medium">{t.to_status}</span> · {t.actor}
                      {t.note && <span className="text-bank-muted"> · {t.note}</span>}
                    </span>
                    <span className="text-bank-muted shrink-0 font-mono text-[10.5px] whitespace-nowrap">
                      {formatDate(t.created_at)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Panel>
      </div>

      <Drawer
        open={selectedId !== null}
        onClose={() => setSelectedId(null)}
        title={selectedId !== null ? `Question #${selectedId}` : ""}
      >
        {selectedId !== null && (
          <QuestionDetail id={selectedId} showStatusActions onOpenQuestion={setSelectedId} />
        )}
      </Drawer>
    </div>
  );
}

function quotaPause(job: JobOut | undefined): { model: string } | null {
  if (!job || (job.status !== "queued" && job.status !== "running")) return null;
  const cursor = job.cursor as { paused_reason?: string; quota_model?: string } | null;
  if (cursor?.paused_reason !== "quota") return null;
  return { model: cursor.quota_model ?? "unknown model" };
}
